<script setup lang="ts">
import type { NavButton, NavButtonSection } from "~/composables/useNavBarData";
import { getNavButtonActivePath } from "~/composables/useNavBarData";

const props = defineProps<{
  section: NavButtonSection;
}>();

const collapseContent = ref<HTMLElement>();
const isNotificationSheetOpened = ref(false);

const route = useRoute();
const isFromFavorites = computed(() => route.query.favorite === "true");

const sectionId = computed(() => `nav-section-${props.section.name.toLowerCase().replace(/\s+/g, "-")}`);
const savedStates = useLocalStorage<Record<string, boolean>>("nav-sections-state", {});

const isOpen = computed({
  get: () => {
    if (savedStates.value && sectionId.value in savedStates.value) {
      return savedStates.value[sectionId.value];
    }
    return true;
  },
  set: (value: boolean) => {
    if (!savedStates.value) {
      savedStates.value = {};
    }
    savedStates.value[sectionId.value] = value;
  },
});

const toggleOpen = () => {
  isOpen.value = !isOpen.value;
};

const getIsActive = (button: NavButton, isActive: boolean) => {
  if (button.to.includes("?favorite=true")) {
    return isActive && isFromFavorites.value && !isNotificationSheetOpened.value;
  } else {
    const baseRoute = route.path.split("?")[0];
    const routeMatches = baseRoute.startsWith(getNavButtonActivePath(button));
    const isLinkActive = isActive || button.activePath !== undefined;

    return isLinkActive && routeMatches && !isNotificationSheetOpened.value && !isFromFavorites.value;
  }
};

const onEnter = (element: Element) => {
  if (!(element instanceof HTMLElement)) return;
  element.style.height = "0px";
  void element.offsetHeight;
  element.style.height = element.scrollHeight + "px";
};

const onLeave = (element: Element) => {
  if (!(element instanceof HTMLElement)) return;
  element.style.height = element.scrollHeight + "px";
  void element.offsetHeight;
  element.style.height = "0px";
};
</script>

<template>
  <div class="nav-section-title" @click="toggleOpen">
    <AwBadge
      style="padding: 0px; display: flex; align-items: center; justify-content: center"
      size="sm"
      :color="section.color"
      class="nav-section-title-badge"
    >
      <AwIcon :name="section.icon" class="nav-section-title-badge-icon" :color="section.color" />
    </AwBadge>
    <div class="font-semibold" style="color: #959595">
      {{ section.name }}
    </div>
    <AwIcon :name="`${isOpen ? 'i-ph-caret-down-fill' : 'i-ph-caret-up-fill'}`" class="nav-section-title-caret" />
  </div>

  <transition name="collapse" @enter="onEnter" @leave="onLeave">
    <div v-show="isOpen" ref="collapseContent">
      <div v-if="section.items.length > 0">
        <div v-for="button in section.items" :key="button.to" class="nav-section-content-container">
          <NuxtLink v-slot="{ isActive }" :to="button.to">
            <NavButton
              :icon="button.icon"
              :is-active="getIsActive(button, isActive)"
              :is-child="button.isChild"
              :title="button.label"
              :notification-count="button.count"
            />
          </NuxtLink>
        </div>
      </div>
      <div v-else class="nav-section-empty-content-container">
        <slot name="empty"></slot>
      </div>
    </div>
  </transition>
</template>

<style scoped lang="scss">
.nav-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 25px;
  cursor: pointer;
}

.nav-section-title-badge {
  width: 17px;
  height: 17px;
}

.nav-section-title-badge-icon {
  width: 10px;
  height: 10px;
}

.nav-section-title-caret {
  width: 10px;
  height: 14px;
}

.nav-section-content-container {
  display: flex;
  flex-direction: column;
  border-left: 1px solid var(--gray-5);
  box-sizing: border-box;
  margin-left: 8px;
}

.nav-section-empty-content-container {
  display: flex;
  gap: 8px;
  box-sizing: border-box;
  border: dashed 1px var(--gray-5);
  border-radius: 8px;
}

.collapse-enter-active,
.collapse-leave-active {
  transition:
    height 0.3s ease,
    opacity 0.3s ease;
  overflow: hidden;
}

.collapse-enter-from,
.collapse-leave-to {
  opacity: 0;
}
</style>
